import { addDays } from './analyticsUi';

// How daily ad budget turns into new users and profit. Two measurements from the daily data:
//   - Budget periods: average signups per day at each budget level, and what the extra signups of
//     one level over the previous one cost (spend difference / signup difference).
//   - Response curve: weekly signups = base + trend + b · spend^θ (θ ≤ 1 = diminishing returns),
//     fitted on weekly totals so weekday swings cancel out. Its slope at a spend level is the
//     number of extra users the next € buys, and the best budget is where one extra user costs as
//     much as a user earns. Ranges come from refitting on weeks drawn at random.
// Both count all new users, not only those Google credits, so they include users ads bring in
// indirectly and leave out users ads would have "taken credit" for anyway.

const FIT_WEEKS = 52;
const MIN_WEEKS = 8;
// Spend must vary this much between weeks (coefficient of variation) for the curve to mean anything
const MIN_SPEND_CV = 0.1;
// Returns always flatten eventually; θ = 1 (every € as good as the last, forever) is left out
const THETAS = [0.4, 0.5, 0.6, 0.7, 0.8, 0.9];
const BOOT_RUNS = 300;
// Days after a budget change that are left out while Google's campaign settles
const SETTLE_DAYS = 3;
// Each extra € must come back as this much: a cushion for forecast error and for the year it takes
// a user to earn their value. The recommended budget stops where one more user costs value / this.
export const TARGET_RETURN = 1.3;
const MIN_PERIOD_DAYS = 7;
// Measured days a new budget needs before the forecast continues at its pace
const MIN_PACE_DAYS = 5;
// Spend the newest days' network split may still lack before the day counts as not split yet
const SPLIT_TOLERANCE = 0.5;
export const UNASSIGNED_NETWORK = 'UNASSIGNED';

function seededRandom(seed) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const quantile = (sorted, q) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.round(q * (sorted.length - 1))))];

// Least squares for y = X·coef by the normal equations (a handful of columns, so Gauss-Jordan is fine)
function leastSquares(X, y) {
  const k = X[0].length;
  const A = Array.from({ length: k }, (_, i) => {
    const row = Array.from({ length: k }, (__, j) => X.reduce((s, x) => s + x[i] * x[j], 0));
    row.push(X.reduce((s, x, n) => s + x[i] * y[n], 0));
    return row;
  });
  for (let c = 0; c < k; c++) {
    let p = c;
    for (let r = c + 1; r < k; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
    if (Math.abs(A[p][c]) < 1e-9) return null;
    [A[c], A[p]] = [A[p], A[c]];
    for (let r = 0; r < k; r++) {
      if (r === c) continue;
      const f = A[r][c] / A[c][c];
      for (let j = c; j <= k; j++) A[r][j] -= f * A[c][j];
    }
  }
  const coef = A.map((row, i) => row[k] / row[i]);
  const sse = X.reduce((s, x, n) => s + (y[n] - x.reduce((a, v, j) => a + v * coef[j], 0)) ** 2, 0);
  return { coef, sse };
}

// Spend is scaled to €100/week so spend^θ stays in a sensible range for every θ
const SCALE = 100;

function fitCurve(weeks, withTrend) {
  let best = null;
  THETAS.forEach(theta => {
    const X = weeks.map(w => [1, ...(withTrend ? [w.t] : []), (w.spend / SCALE) ** theta]);
    const fit = leastSquares(X, weeks.map(w => w.signups));
    if (fit && (!best || fit.sse < best.sse)) best = { theta, beta: fit.coef[fit.coef.length - 1], sse: fit.sse };
  });
  return best;
}

// Extra users per extra € at a weekly spend
const marginalUsers = (fit, weekSpend) => (fit.beta * fit.theta * (Math.max(weekSpend, 1) / SCALE) ** (fit.theta - 1)) / SCALE;
// Profit per day from ads at a daily budget, compared with no ads: value of the extra users minus spend
const dailyProfit = (fit, value, budget) => (value * fit.beta * ((budget * 7) / SCALE) ** fit.theta) / 7 - budget;

function bestBudget(fit, value, maxBudget) {
  if (!(fit.beta > 0)) return 0;
  if (fit.theta >= 1) return value * marginalUsers(fit, 0) > 1 ? maxBudget : 0;
  const weekly = SCALE * ((value * fit.beta * fit.theta) / SCALE) ** (1 / (1 - fit.theta));
  return Math.min(maxBudget, weekly / 7);
}

function weeklySeries(daily, lastDay) {
  const byDate = {};
  daily.forEach(d => { byDate[d.date] = d; });
  const first = daily[0]?.date;
  const weeks = [];
  for (let end = lastDay; weeks.length < FIT_WEEKS; end = addDays(end, -7)) {
    const start = addDays(end, -6);
    if (!first || start < first) break;
    let spend = 0;
    let signups = 0;
    for (let k = start; k <= end; k = addDays(k, 1)) {
      spend += byDate[k]?.spend || 0;
      signups += byDate[k]?.signups || 0;
    }
    weeks.push({ start, end, spend, signups });
  }
  weeks.reverse().forEach((w, i) => { w.t = i; });
  return weeks;
}

function responseCurve(weeks, value, maxBudget) {
  if (weeks.length < MIN_WEEKS) return { reason: `needs ${MIN_WEEKS} weeks of data, has ${weeks.length}` };
  const spends = weeks.map(w => w.spend);
  const mean = spends.reduce((s, v) => s + v, 0) / spends.length;
  const cv = mean ? Math.sqrt(spends.reduce((s, v) => s + (v - mean) ** 2, 0) / spends.length) / mean : 0;
  if (cv < MIN_SPEND_CV) return { reason: 'weekly spend has stayed almost the same, so its effect can’t be told apart from normal ups and downs', cv };

  const withTrend = weeks.length >= 12;
  const fit = fitCurve(weeks, withTrend);
  if (!fit) return { reason: 'the fit failed' };

  const random = seededRandom(4242);
  const boots = [];
  for (let run = 0; run < BOOT_RUNS; run++) {
    const sample = weeks.map(() => weeks[Math.floor(random() * weeks.length)]);
    const f = fitCurve(sample, withTrend);
    if (f) boots.push(f);
  }
  const range = (fn) => {
    const v = boots.map(fn).filter(Number.isFinite).sort((a, b) => a - b);
    return v.length ? { low: quantile(v, 0.05), mid: quantile(v, 0.5), high: quantile(v, 0.95) } : null;
  };
  // Share of the last 4 weeks' new users that came with the spend (the rest is the base level)
  const lastWeeks = weeks.slice(-4);
  const total = lastWeeks.reduce((s, w) => s + w.signups, 0);
  const adsShare = (f) => (total ? lastWeeks.reduce((s, w) => s + f.beta * (w.spend / SCALE) ** f.theta, 0) / total : null);
  const minDaily = Math.min(...spends) / 7;
  const maxDaily = Math.max(...spends) / 7;
  const step = maxBudget / 40;
  const chart = [];
  for (let b = 0; b <= maxBudget + 1e-9; b += step) {
    const p = range(f => dailyProfit(f, value, b));
    chart.push({ budget: b, profit: dailyProfit(fit, value, b), band: p ? [p.low, p.high] : null });
  }
  return {
    fit,
    weeks: weeks.length,
    cv,
    tested: { low: minDaily, high: maxDaily },
    best: { value: bestBudget(fit, value / TARGET_RETURN, maxBudget), range: range(f => bestBudget(f, value / TARGET_RETURN, maxBudget)) },
    adsShare: { value: adsShare(fit), range: range(adsShare) },
    // Profit per day at a budget, and its 90% range
    profitAt: (b) => {
      const p = range(f => dailyProfit(f, value, b));
      return { value: dailyProfit(fit, value, b), low: p?.low ?? null, high: p?.high ?? null };
    },
    marginalCostAt: (dailyBudget) => {
      const m = marginalUsers(fit, dailyBudget * 7);
      const r = range(f => marginalUsers(f, dailyBudget * 7));
      // A range on users per € flips into a range on cost per user; zero or negative means "no effect"
      const cost = (u) => (u > 0 ? 1 / u : Infinity);
      return { value: cost(m), low: r ? cost(r.high) : null, high: r ? cost(r.low) : null };
    },
    chart,
  };
}

// Daily budget changes from the Google Ads change history, as the first day each budget applied.
// Changes made in the evening count from the next day. Google's own budget data only reaches back
// a short way, so this covers the time before it; its later entries are appended.
const KNOWN_BUDGETS = [
  { date: '2026-01-15', budget: 20 },
  { date: '2026-01-22', budget: 14 },
  { date: '2026-01-26', budget: 5 },
  { date: '2026-02-08', budget: 2 },
  { date: '2026-02-22', budget: 7 },
  { date: '2026-03-14', budget: 2 },
  { date: '2026-09-16', budget: 6 },
  { date: '2026-09-21', budget: 18 },
  { date: '2026-09-27', budget: 13 },
];

const withKnownBudgets = (budgets) => {
  const lastKnown = KNOWN_BUDGETS[KNOWN_BUDGETS.length - 1].date;
  return [...KNOWN_BUDGETS, ...budgets.filter(b => b.date > lastKnown)];
};

// The budget in force on each day: from Google Ads budget snapshots when there are any
function budgetByDay(budgets, days) {
  const out = {};
  let i = 0;
  let current = null;
  days.forEach(d => {
    while (i < budgets.length && budgets[i].date <= d.date) current = budgets[i++].budget;
    if (current != null) out[d.date] = current;
  });
  return out;
}

// Runs of days at one budget (or between two markers when there's no budget history)
function findPeriods(days, budgetOf, markers) {
  const runs = [];
  if (Object.keys(budgetOf).length) {
    days.forEach(d => {
      const b = budgetOf[d.date];
      if (b == null) return;
      const last = runs[runs.length - 1];
      if (last && Math.abs(last.budget - b) < 0.5 && addDays(last.to, 1) === d.date) last.to = d.date;
      else runs.push({ from: d.date, to: d.date, budget: b, label: null });
    });
  } else {
    const cuts = [...new Set(markers.map(m => m.date))].filter(k => k > days[0].date && k <= days[days.length - 1].date).sort();
    let from = days[0].date;
    cuts.forEach(cut => {
      runs.push({ from, to: addDays(cut, -1), budget: null, label: null });
      from = cut;
    });
    runs.push({ from, to: days[days.length - 1].date, budget: null, label: null });
    runs.forEach(r => { r.label = markers.find(m => m.date === r.from)?.note || null; });
  }

  const byDate = {};
  days.forEach(d => { byDate[d.date] = d; });
  return runs.map(r => {
    const from = runs[0] === r ? r.from : addDays(r.from, SETTLE_DAYS);
    let n = 0;
    let spend = 0;
    let signups = 0;
    let credited = 0;
    for (let k = from; k <= r.to; k = addDays(k, 1)) {
      if (!byDate[k]) continue;
      n++;
      spend += byDate[k].spend;
      signups += byDate[k].signups;
      credited += byDate[k].installsPaid;
    }
    return { ...r, measuredFrom: from, days: n, spendPerDay: n ? spend / n : 0, signupsPerDay: n ? signups / n : 0, creditedPerDay: n ? credited / n : 0, signups };
  });
}

const daysBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000);

// The latest budget change, measured against the period before it: how far along it is, how many
// days it needs (same rule as testPlan) and what the extra (or lost) users cost so far
function budgetTest(runs, periods, lastDay, limit) {
  const run = runs[runs.length - 1];
  const prev = [...periods].reverse().find(p => p !== run && p.from < run.from);
  if (!run || run.budget == null || !prev || Math.abs(run.budget - prev.budget) < 0.5) return null;
  const daysSoFar = daysBetween(run.from, lastDay) + 1;
  const extraUsers = Math.abs(run.budget - prev.budget) / limit;
  const needed = SETTLE_DAYS + Math.max(14, Math.ceil((2 * prev.signupsPerDay * (1.282 + 0.842) ** 2) / extraUsers ** 2));
  // A test that ended a while ago is just another row in the periods table
  if (daysSoFar > needed + 14) return null;
  const out = { from: prev, to: run, daysSoFar, needed, done: daysSoFar >= needed, measuring: run.days > 0 };
  if (!out.measuring) return out;
  const c = comparePeriods(prev, run);
  // Cost per user gained (or € saved per user lost on a cut), with its range when the change is clear
  const costOf = (users) => (users !== 0 && c.dSpend / users > 0 ? c.dSpend / users : null);
  const clear = c.lowUsers > 0 || c.highUsers < 0;
  const ends = clear ? [costOf(c.lowUsers), costOf(c.highUsers)].filter(v => v != null).sort((a, b) => a - b) : [];
  return {
    ...out,
    ...c,
    clear,
    cost: clear ? costOf(c.dUsers) : null,
    costRange: ends.length === 2 ? { low: ends[0], high: ends[1] } : null,
  };
}

// Cost of the extra users between two periods, with a 90% range on the signup difference from
// day-to-day noise (signups counted as Poisson)
function comparePeriods(prev, next) {
  const dSpend = next.spendPerDay - prev.spendPerDay;
  const dUsers = next.signupsPerDay - prev.signupsPerDay;
  const se = Math.sqrt(next.signups / next.days ** 2 + prev.signups / prev.days ** 2);
  const lowUsers = dUsers - 1.645 * se;
  const highUsers = dUsers + 1.645 * se;
  const measurable = Math.abs(dSpend) >= 1 && (lowUsers > 0 || highUsers < 0);
  return { dSpend, dUsers, lowUsers, highUsers, measurable, cost: measurable && dUsers / dSpend > 0 ? dSpend / dUsers : null };
}

const roundBudget = (v) => (v < 20 ? Math.round(v) : Math.round(v / 5) * 5);
const budgetRange = (r) => (r.low < 1 ? `under ${roundBudget(r.high)} €` : `${roundBudget(r.low)}–${roundBudget(r.high)} €`);

// Doubling (or halving) the budget, and how many days each level needs before the signup
// difference stands out from noise (90% one-sided, 80% power), if the extra users cost exactly
// what a user earns
function testPlan(base, signupsPerDay, value, down = false) {
  const to = roundBudget(down ? base / 2 : base * 2);
  const extraUsers = Math.abs(to - base) / value;
  const days = Math.ceil((2 * signupsPerDay * (1.282 + 0.842) ** 2) / extraUsers ** 2);
  return { to, days: Math.max(14, days) };
}
const signed = (v) => `${v < 0 ? '−' : '+'}${Math.abs(Math.round(v * 10) / 10).toLocaleString('fi-FI')}`;
const planText = (plan) => (plan.days <= 60
  ? `Test ${plan.to} € a day for ${plan.days} days.`
  : `A test at ${plan.to} € a day would take about ${plan.days} days.`)
  + (plan.profitChange ? ` Profit likely ${signed(plan.profitChange.low)} to ${signed(plan.profitChange.high)} € a day, plus more users.` : '');

// Google Ads' own delivery numbers per day over a window: impressions, clicks, its conversion count
function deliveryWindow(delivery, days, from, to) {
  const rows = delivery.filter(r => r.date >= from && r.date <= to);
  const ours = days.filter(d => d.date >= from && d.date <= to);
  if (!rows.length || !ours.length) return null;
  const sum = (list, k) => list.reduce((s, r) => s + (r[k] || 0), 0);
  const impressions = sum(rows, 'impressions');
  const clicks = sum(rows, 'clicks');
  const conversions = sum(rows, 'conversions');
  const spend = sum(ours, 'spend');
  return {
    impressions: impressions / rows.length,
    conversions: conversions / rows.length,
    credited: sum(ours, 'installsPaid') / ours.length,
    cpm: impressions ? (spend / impressions) * 1000 : null,
    ctr: impressions ? clicks / impressions : null,
    costPerConversion: conversions ? spend / conversions : null,
  };
}

// Totals per network over a window, biggest spend first. Spend Google hasn't split yet (its newest
// days lag behind the campaign total) gets a row of its own, so the table adds up to what was spent.
function networksBetween(networkDaily, days, from, to) {
  const totals = {};
  networkDaily.forEach(r => {
    if (r.date < from || r.date > to) return;
    const t = totals[r.network] || (totals[r.network] = { network: r.network, spend: 0, impressions: 0, clicks: 0, conversions: 0, viewThrough: 0 });
    ['spend', 'impressions', 'clicks', 'conversions', 'viewThrough'].forEach(k => { t[k] += r[k] || 0; });
  });
  const list = Object.values(totals).sort((a, b) => b.spend - a.spend);
  const spent = days.filter(d => d.date >= from && d.date <= to).reduce((s, d) => s + d.spend, 0);
  const gap = spent - list.reduce((s, n) => s + n.spend, 0);
  if (gap > SPLIT_TOLERANCE) list.push({ network: UNASSIGNED_NETWORK, spend: gap, impressions: null, clicks: null, conversions: null, viewThrough: null });
  return list;
}

// Where a budget change's money went: spend and conversions per day on each network before and
// after it, using only days Google has fully split
function networkShift(networkDaily, days, prev, run) {
  const split = {};
  networkDaily.forEach(r => { split[r.date] = (split[r.date] || 0) + r.spend; });
  const splitDays = (p) => days
    .filter(d => d.date >= p.measuredFrom && d.date <= p.to && d.date in split && Math.abs(split[d.date] - d.spend) <= SPLIT_TOLERANCE)
    .map(d => d.date);
  const before = splitDays(prev);
  const after = splitDays(run);
  if (!before.length || !after.length) return null;
  const perDay = (dates, network, key) => {
    const set = new Set(dates);
    return networkDaily.filter(r => r.network === network && set.has(r.date)).reduce((s, r) => s + (r[key] || 0), 0) / dates.length;
  };
  const rows = [...new Set(networkDaily.map(r => r.network))].map(network => {
    const spendBefore = perDay(before, network, 'spend');
    const spendAfter = perDay(after, network, 'spend');
    const conversionsBefore = perDay(before, network, 'conversions');
    const conversionsAfter = perDay(after, network, 'conversions');
    const extra = conversionsAfter - conversionsBefore;
    return {
      network,
      spendBefore,
      spendAfter,
      conversionsBefore,
      conversionsAfter,
      costPerExtra: extra > 0 && spendAfter > spendBefore ? (spendAfter - spendBefore) / extra : null,
    };
  })
    .filter(r => r.spendBefore >= 0.05 || r.spendAfter >= 0.05)
    .sort((a, b) => (b.spendAfter - b.spendBefore) - (a.spendAfter - a.spendBefore));
  return { rows, daysBefore: before.length, daysAfter: after.length };
}

export function buildBudgetAdvice({ daily, adsBudget, markers, value, typical = [], costLow, costHigh, today }) {
  const lastDay = addDays(today, -1);
  const days = daily.filter(d => d.date <= lastDay);
  if (!days.length || !(value > 0)) return null;

  const recent = days.slice(-14);
  const spendNow = recent.reduce((s, d) => s + d.spend, 0) / recent.length;
  if (!(spendNow > 0)) return null;
  const budgets = withKnownBudgets(adsBudget?.daily || []);
  const budgetOf = budgetByDay(budgets, days);
  const budgetNow = budgetOf[lastDay] ?? null;
  const usedShare = budgetNow ? recent.reduce((s, d) => s + d.spend, 0) / recent.reduce((s, d) => s + (budgetOf[d.date] || budgetNow), 0) : null;
  const is = (adsBudget?.impressionShare || []).filter(r => r.date >= addDays(lastDay, -13));
  const lostToBudget = is.length ? is.reduce((s, r) => s + r.lostBudget, 0) / is.length : null;
  const appOnly = (adsBudget?.channelTypes || []).length > 0 && adsBudget.channelTypes.every(t => /APP|MULTI_CHANNEL/.test(t));

  const runs = findPeriods(days, budgetOf, markers);
  const periods = runs.filter(p => p.days >= MIN_PERIOD_DAYS);
  const steps = periods.slice(1).map((p, i) => ({ ...comparePeriods(periods[i], p), from: periods[i], to: p }));
  // The budget running now when it's too new for its own row in the table
  const latest = runs[runs.length - 1];
  const current = latest && !periods.includes(latest) && latest.budget != null ? { ...latest, daysSoFar: daysBetween(latest.from, lastDay) + 1 } : null;

  // Daily new users, credited installs and spend next to the budget in force, with 7-day averages
  // to smooth weekday swings (none until there are 7 days)
  const history = days.map((d, i) => {
    const win = i >= 6 ? days.slice(i - 6, i + 1) : null;
    const avg = (k) => (win ? win.reduce((s, x) => s + x[k], 0) / 7 : null);
    return {
      date: d.date,
      signups: d.signups,
      average: avg('signups'),
      credited: avg('installsPaid'),
      spend: avg('spend'),
      budget: budgetOf[d.date] ?? null,
    };
  });

  const base = budgetNow || spendNow;
  const maxBudget = Math.max(base * 4, 20);
  const curve = responseCurve(weeklySeries(days, lastDay), value, maxBudget);
  const marginal = curve.fit ? curve.marginalCostAt(spendNow) : null;
  const stepUp = roundBudget(base * 1.5);
  const stepDown = roundBudget(base * 0.67);
  const signupsNow = recent.reduce((s, d) => s + d.signups, 0) / recent.length;
  const limit = value / TARGET_RETURN;
  // Test upward (more users) unless the curve says doubling probably loses money and the best budget is lower
  const upProfit = curve.fit ? curve.profitAt(roundBudget(base * 2)) : null;
  const nowProfit = curve.fit ? curve.profitAt(base).value : null;
  const doublingLoses = !!upProfit && upProfit.value < nowProfit - 0.25;
  const plan = testPlan(base, signupsNow, limit, !!curve.fit && doublingLoses && curve.best.value < base * 0.85);
  if (curve.fit && plan.to > base && upProfit?.low != null) {
    plan.profitChange = { low: upProfit.low - nowProfit, high: upProfit.high - nowProfit };
  }
  const test = budgetTest(runs, periods, lastDay, limit);
  // A running test comes first: starting another would spoil both
  const nextStep = test && !test.done
    ? `Let the ${roundBudget(test.to.budget)} € test run first (day ${test.daysSoFar} of ${test.needed}).`
    : planText(plan);

  // When a user pays for themselves, at the cost of the next user and at the average costs
  const recentSpend = recent.reduce((s, d) => s + d.spend, 0);
  const recentSignups = recent.reduce((s, d) => s + d.signups, 0);
  const recentCredited = recent.reduce((s, d) => s + d.installsPaid, 0);
  const paybackOf = (cost) => typical.find(p => p.value >= cost) || null;
  const payback = [
    { key: 'marginal', label: 'One more user', cost: marginal?.value },
    { key: 'all', label: 'Average new user', cost: recentSignups ? recentSpend / recentSignups : null },
    { key: 'credited', label: 'Average credited install', cost: recentCredited ? recentSpend / recentCredited : null },
  ]
    .filter(c => c.cost > 0 && Number.isFinite(c.cost))
    .map(c => {
      const p = paybackOf(c.cost);
      return { ...c, age: p?.age ?? null, extrapolated: !!p?.extrapolated };
    });

  const delivery = adsBudget?.delivery || [];
  const targets = (adsBudget?.bidTargets || []).filter(t => t.date <= lastDay);
  const bidTarget = targets.length ? targets[targets.length - 1] : null;
  // The day the target last changed; unknown if it's been the same for all the history there is
  const bidTargetSince = bidTarget ? [...targets].reverse().find((t, i, arr) => arr[i + 1] && Math.abs(arr[i + 1].target - bidTarget.target) > 0.005)?.date ?? null : null;
  const networkDaily = adsBudget?.networkDaily || [];
  const ads = delivery.length || bidTarget || networkDaily.length
    ? {
      now: deliveryWindow(delivery, days, addDays(lastDay, -13), lastDay),
      prev: deliveryWindow(delivery, days, addDays(lastDay, -27), addDays(lastDay, -14)),
      networks: networksBetween(networkDaily, days, addDays(lastDay, -29), lastDay),
      networkShift: test?.measuring && networkDaily.length ? networkShift(networkDaily, days, test.from, test.to) : null,
      bidTarget: bidTarget ? { ...bidTarget, since: bidTargetSince } : null,
      biddingStrategies: adsBudget?.biddingStrategies || [],
    }
    : null;

  let status;
  let title;
  let text;
  if (usedShare != null && usedShare < 0.8) {
    status = 'warn';
    title = 'Google isn’t spending the whole budget';
    text = `Only ${Math.round(usedShare * 100)}% of the budget gets spent. Raise the bid target${bidTarget ? ` (now ${bidTarget.target.toLocaleString('fi-FI', { minimumFractionDigits: 2 })} €)` : ''} rather than the budget.`;
  } else if (curve.fit && curve.best.range) {
    const r = curve.best.range;
    const beyond = curve.best.value > curve.tested.high * 1.5;
    if (r.low > base * 1.15) {
      status = 'good';
      title = 'Room to raise the budget';
      text = `Best budget is ${beyond ? `over ${roundBudget(curve.tested.high * 1.5)} €` : `about ${roundBudget(curve.best.value)} €`} (likely ${budgetRange(r)}). Try ${Math.min(stepUp, roundBudget(Math.max(r.low, base * 1.2)))} € a day for a few weeks.`;
    } else if (r.high < base * 0.85) {
      status = 'bad';
      title = 'The budget is probably too high';
      text = `The last euros buy users costing over ${Math.round(limit * 100) / 100} €. Best budget is about ${roundBudget(curve.best.value)} € (likely ${budgetRange(r)}).`;
    } else if (r.low >= base * 0.6 && r.high <= base * 1.6) {
      status = 'good';
      title = 'The budget is about right';
      text = `Best budget is likely ${budgetRange(r)}; you’re at ${roundBudget(base)} €.`;
    } else {
      status = 'info';
      title = 'Not clear yet';
      text = `Best budget unclear (likely ${budgetRange(r)}). ${nextStep}`;
    }
  } else if (costLow > limit) {
    status = 'bad';
    title = 'Lower the budget';
    text = `Even counting every new user as bought, a user costs ${Math.round(costLow * 100) / 100} €, over the ${Math.round(limit * 100) / 100} € limit. Try ${stepDown} € a day.`;
  } else {
    status = 'info';
    title = 'Run a budget test to find out';
    const headroom = costHigh ? value / costHigh : value / costLow;
    text = `Budget has been too steady to measure. ${headroom >= 1.5 ? `A user earns ${headroom.toFixed(1)}× their cost, so there’s likely room to grow.` : "The margin is thin."} ${nextStep}`;
  }

  return {
    status,
    title,
    text,
    spendNow,
    budgetNow,
    usedShare,
    lostToBudget,
    appOnly,
    value,
    limit,
    lastDay,
    periods,
    current,
    steps,
    test,
    history,
    payback,
    ads,
    curve,
    marginal,
    plan,
    pace: latest?.days >= MIN_PACE_DAYS ? { from: latest.from, signupsPerDay: latest.signupsPerDay, spendPerDay: latest.spendPerDay } : null,
    fromBudgetHistory: budgets.length > 0,
  };
}
